import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  Input,
  Box,
  ModalFooter,
  Button,
  Grid,
  Spacer,
  TextArea,
  Switch,
  Select,
  Text,
  Notify,
} from "@flexisaf/flexibull2";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";

import { selectIsCreatingPaper } from "../../../redux/paper/selectors";
import {
  createPaperCurriculumStart,
  editPaperCurriculumStart,
  editPaperStart,
} from "../../../redux/paper/reducer";

import useExamGroupsGet from "../../../hooks/exam-groups/useExamGroupsGet";
import useExamsGet from "../../../hooks/exams/useExamsGet";
import useSubjectsGet from "../../../hooks/subjects/useSubjectsGet";

import { Liner } from "../../../components/custom/wrappers";
import Theme from "../../../theme";
import useSinglePaperGet from "../../../hooks/papers/useSinglePaperGet";
import SectionLoader from "../../../components/custom/sectionLoader";
import { FileUploader } from "../../../components/fileUploader";
import { DefaultCurriculum } from "../../../utils/constants";

const getDefaultPaperInfo = () => ({
  examGroup: null,
  exam: null,
  name: "",
  subject: null,
  duration: "",
  instruction: "",
  active: false,
  curriculumType: null,
});

const PaperEditor = ({ open, data, cbProps, closeModal }) => {
  const [file, setFile] = useState(null);
  const [toggleFileUpdate, setToggleFileUpdate] = useState(false);
  const isCreating = useSelector(selectIsCreatingPaper);

  const { examGroups, loadingExamGroups, currentExamGroup } =
    useExamGroupsGet();
  const { loadingSubjects, subjects } = useSubjectsGet();

  const {
    paper,
    loadingPaper,
    resetLoading,
    refetch: refetchPaper,
  } = useSinglePaperGet({
    id: data?.id,
  });

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      ...getDefaultPaperInfo(),
    },
  });
  const { exams, loadingExams, currentExam } = useExamsGet({
    examGroupId: watch("examGroup")?.value,
  });

  useEffect(() => {
    if (paper) {
      reset({
        name: paper?.name,
        duration: paper?.duration,
        subject: {
          label: paper?.subject?.name,
          value: paper?.subject?.id,
        },
        instruction: paper?.instruction,
        exam: {
          label: paper?.exam?.name,
          value: paper?.exam?.id,
        },
        examGroup: {
          label: paper?.exam?.examGroup?.name,
          value: paper?.exam?.examGroup?.id,
        },
        active: paper?.isActive,
        curriculumType: paper?.curriculum
          ? {
              label: paper?.curriculum,
              value: paper?.curriculum,
            }
          : null,
      });
    }

    if (open && !data) {
      reset(getDefaultPaperInfo());
    }
  }, [paper, reset, data, open]);

  useEffect(() => {
    if (currentExam && currentExamGroup && !data) {
      setValue("exam", {
        value: currentExam?.id,
        label: currentExam?.name,
      });
      setValue("examGroup", {
        label: currentExamGroup?.name,
        value: currentExamGroup?.id,
      });
    }
  }, [currentExam, currentExamGroup, setValue, data, open]);

  const handleClose = () => {
    reset(getDefaultPaperInfo());
    resetLoading();
    closeModal();
    document.body.style.overflow = "auto";
  };

  const handleFile = (e) => {
    e.stopPropagation();

    const file = e.target.files[0];
    const uploadLimit = file.size / 1024 / 1024 < 2.5;

    if (!uploadLimit) {
      Notify("File must not exceed 2.5MB", { status: "error" });
      return;
    } else if (file.type !== "application/pdf") {
      Notify("Invalid file, please select a pdf file", { status: "error" });
      setFile(null);
    } else {
      setFile(file);
    }
  };
  const editPaper = (payload) => {
    dispatch(editPaperStart(payload));
  };
  const editCurriculum = (payload) => {
    dispatch(editPaperCurriculumStart(payload));
  };

  const onCreate = (values) => {
    if (!file)
      return Notify("Please select a curriculum file for upload", {
        status: "error",
      });

    let payload = {
      data: {
        name: values.name,
        duration: values.duration,
        instruction: values.instruction,
        subjectId: values.subject.value,
        active: values.active,
        examId: values?.exam?.value,
        curriculum: values.curriculumType.value,
        curriculumUrl: file,
      },
      callback: () => {
        cbProps.refetchPapers();
        closeModal();
      },
    };
    dispatch(createPaperCurriculumStart(payload));
  };

  const onEdit = (values) => {
    if (!paper.curriculumUrl && !file) {
      return Notify("Please select a curriculum file for upload", {
        status: "error",
      });
    }

    let payload = {
      data: {
        name: values.name,
        duration: values.duration,
        instruction: values.instruction,
        subjectId: values.subject.value,
        active: values.active,
        examId: values?.exam?.value,
        id: paper?.id,
        curriculum: values.curriculumType.value,
        curriculumUrl: file ? file : paper?.curriculumUrl,
        ...(file ? { prevCurriculumUrl: paper.curriculumUrl } : {}),
      },
      callback: () => {
        if (cbProps?.refetchPapers) {
          cbProps.refetchPapers();
        }
        if (cbProps?.refetchPaper) {
          refetchPaper();
        }
        closeModal();
      },
    };
    file ? editCurriculum(payload) : editPaper(payload);
  };

  return (
    <Modal outerClick onClose={handleClose} open={open}>
      <ModalBody width="40vw">
        {data && loadingPaper ? (
          <SectionLoader height="60vh" />
        ) : (
          <>
            <Box
              pad="20px"
              style={{ borderBottom: `1px solid ${Theme.PrimaryBorderColor}` }}
            >
              <h3>{`${data ? "Edit" : "Add New"} Paper`}</h3>
            </Box>
            <Spacer space={20} />
            <Box pad="2rem">
              <Grid default="1fr 1fr">
                <Input
                  name="name"
                  placeholder="Ex: Mathematics"
                  label="Paper name"
                  maxLength={128}
                  required
                  // disabled={edit}
                  value={watch("name")}
                  {...register("name", {
                    required: "Please enter a name for your paper",
                  })}
                  error={errors?.name?.message}
                  onChange={({ target: { value } }) => setValue("name", value)}
                />
                <Input
                  label="Duration (Mins)"
                  placeholder="30"
                  type="number"
                  required
                  value={watch("duration")}
                  {...register("duration", {
                    required: "Enter a duration",
                  })}
                  error={errors?.duration?.message}
                  onChange={({ target: { value } }) =>
                    setValue("duration", value)
                  }
                />
              </Grid>
              <Spacer space={50} />
              <Grid default="1fr 1fr">
                <Select
                  label="Subject"
                  required
                  isLoading={loadingSubjects}
                  disable={loadingSubjects}
                  block
                  placeholder="Select Subject"
                  options={subjects?.map((item) => {
                    return {
                      label: item.name,
                      value: item.id,
                    };
                  })}
                  value={watch("subject")}
                  {...register("subject", {
                    required: "Select a subject",
                  })}
                  error={errors?.subject?.message}
                  onChange={(value) => setValue("subject", value)}
                />
                <Select
                  label="Paper Curriculum"
                  required
                  block
                  placeholder="Select curriculum type"
                  options={DefaultCurriculum?.map((item) => {
                    return {
                      label: item.label,
                      value: item.value,
                    };
                  })}
                  value={watch("curriculumType")}
                  {...register("curriculumType", {
                    required: "Select a curriculum type",
                  })}
                  error={errors?.curriculumType?.message}
                  onChange={(value) => setValue("curriculumType", value)}
                />
              </Grid>

              <Spacer space={50} />
              <Liner />
              <Spacer space={50} />

              <Grid default="1fr 1fr">
                <Select
                  label="Exam Group"
                  block
                  required
                  isLoading={loadingExamGroups}
                  disable={loadingExamGroups}
                  value={watch("examGroup")}
                  options={examGroups?.items?.map((group) => {
                    return {
                      label: group?.name,
                      value: group?.id,
                    };
                  })}
                  {...register("examGroup", {
                    required: "Please select an exam group",
                  })}
                  error={errors.examGroup?.message}
                  onChange={(value) => setValue("examGroup", value)}
                />

                <Select
                  isLoading={loadingExams}
                  disable={loadingExams}
                  value={watch("exam")}
                  options={exams?.items?.map((exam) => {
                    return {
                      label: exam?.name,
                      value: exam?.id,
                    };
                  })}
                  {...register("exam", {
                    required: "Please select an exam",
                  })}
                  error={errors.exam?.message}
                  onChange={(value) => setValue("exam", value)}
                  label="Exam"
                  block
                />
              </Grid>
              <Spacer space={50} />

              <Grid default="1fr">
                <TextArea
                  name="instruction"
                  height="100px"
                  block
                  placeholder=""
                  label="Instruction"
                  required
                  value={watch("instruction")}
                  {...register("instruction", {
                    required: "Enter an instruction",
                  })}
                  error={errors?.instruction?.message}
                  onChange={({ target: { value } }) =>
                    setValue("instruction", value)
                  }
                />
              </Grid>
              <Spacer space={10} />
              <Box pad="25px 0">
                {paper?.curriculumUrl && !toggleFileUpdate ? (
                  <Button
                    onClick={() => setToggleFileUpdate(!toggleFileUpdate)}
                  >
                    Update curriculum file
                  </Button>
                ) : (
                  <>
                    <FileUploader
                      onChange={(e) => handleFile(e)}
                      label="Select your curriculum file"
                      size="Maximum size 2.5mb"
                      accept=".pdf"
                    />
                    {file ? <p>File: {file.name}</p> : null}
                  </>
                )}
              </Box>
              <Box
                pad="20px 0"
                style={{
                  borderTop: `1px solid ${Theme.PrimaryBorderColor}`,
                }}
              >
                <Text bold>Paper Active</Text>
                <Box />
                <Switch
                  size={20}
                  checked={watch("active")}
                  {...register("active", {})}
                  onChange={({ target: { checked } }) =>
                    setValue("active", checked)
                  }
                  block
                />
              </Box>
            </Box>

            <ModalFooter
              style={{ borderTop: `1px solid ${Theme.PrimaryBorderColor}` }}
            >
              <Box pad="20px">
                <Grid default="1fr 1fr">
                  <Box>
                    <Button pale color="transparent" onClick={handleClose}>
                      Cancel
                    </Button>
                  </Box>
                  <Box align="right">
                    {data ? (
                      <Button
                        progress={isCreating}
                        onClick={handleSubmit((v) => {
                          onEdit(v);
                        })}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        progress={isCreating}
                        onClick={handleSubmit((v) => {
                          onCreate(v);
                        })}
                      >
                        Create
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </ModalFooter>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PaperEditor;
